import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useRealtimeChannel } from "@/lib/realtime";
import { hazardTone } from "@/lib/audio";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import SupervisorCommand from "@/components/SupervisorCommand";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { DiscBadge, PriorityBadge } from "@/components/badges";
import { ShieldAlert, FileText, ClipboardCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FeedbackQrCard } from "@/components/FeedbackQrCard";

function Metric({ label, value, color }) {
  return (
    <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3">
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">{label}</div>
      <div className="font-mono font-bold text-2xl mt-1" style={{ color: color || "#F3F4F6" }}>{value}</div>
    </div>
  );
}

export default function Supervisor() {
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [rt, setRt] = useState(null);
  const [denials, setDenials] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      client.get("/metrics/supervisor"), client.get("/metrics/response-times"),
      client.get("/security/access-denials"),
      client.get("/rms-reports", { params: { status: "PENDING REVIEW" } }).catch(() => ({ data: [] })),
    ]);
    setM(a.data); setRt(b.data); setDenials(c.data || []); setPendingReports(d.data || []);
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useRealtimeChannel((msg) => {
    if (msg.type === "security_alert") {
      const d = msg.denial || {};
      try { hazardTone(); } catch { /* noop */ }
      toast.error(`🚨 ACCESS DENIED — ${d.user || "User"}${d.badge ? ` (${d.badge})` : ""}: ${d.detail || d.action}`, { duration: 7000 });
      load();
    }
  });
  const downloadAllBriefings = async () => {
    try {
      toast.loading("Bundling every patrol unit's roll-call briefing…", { id: "brief-all" });
      const { data } = await client.get("/patrol/briefing-all", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Roll-call briefing packet generated", { id: "brief-all" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate briefings", { id: "brief-all" }); }
  };
  const fmt = (s) => (s || s === 0) ? `${Math.floor(s / 60)}:${String(Math.round(s) % 60).padStart(2, "0")}` : "–";
  const DC = { police: "#3B82F6", fire: "#EF4444", ems: "#10B981" };

  const statusData = m ? Object.entries(m.status_counts).map(([k, v]) => ({ name: k, v })) : [];
  const unitData = m ? Object.entries(m.unit_counts).map(([k, v]) => ({ name: k, v })) : [];
  const SC = { pending: "#F59E0B", dispatched: "#3B82F6", active: "#DC2626", onscene: "#10B981", closed: "#6B7280", available: "#10B981", transport: "#8B5CF6" };

  return (
    <Layout>
      <PageHeader title="Call-Center Supervisor" sub="Live operations metrics · workload · interventions · auto-refresh 8s" />
      <div className="px-4 pt-3"><SupervisorCommand /></div>
      <div className="px-4 pt-3"><FeedbackQrCard isSupervisor /></div>
      <div className="px-4 pt-3">
        <button data-testid="print-all-briefings" onClick={downloadAllBriefings}
          className="flex items-center gap-2 bg-[#0EA5E9] hover:bg-[#0284c7] text-white font-head font-semibold px-4 py-2 text-sm transition-colors">
          <FileText size={15} /> Print All Roll-Call Briefings (PDF)
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 grid-lines">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Metric label="Total Incidents" value={m ? Object.values(m.status_counts).reduce((a, b) => a + b, 0) : "–"} />
          <Metric label="Pending Dispatch" value={m?.status_counts?.pending ?? 0} color="#F59E0B" />
          <Metric label="Active" value={m?.status_counts?.active ?? 0} color="#DC2626" />
          <Metric label="Units Available" value={m?.unit_counts?.available ?? 0} color="#10B981" />
          <button data-testid="metric-pending-reports" onClick={() => nav("/rms-reports")} className="text-left bg-[#141518] border px-4 py-3 transition-colors hover:border-[#F59E0B]"
            style={{ borderColor: pendingReports.length ? "#F59E0B" : "#2C2D35" }}>
            <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">Reports Pending Review</div>
            <div className="font-mono font-bold text-2xl mt-1" style={{ color: pendingReports.length ? "#F59E0B" : "#F3F4F6" }}>{pendingReports.length}</div>
          </button>
        </div>

        <div className="border mt-0 mb-4" style={{ borderColor: pendingReports.length ? "#F59E0B" : "#2C2D35" }} data-testid="pending-report-queue">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center justify-between">
            <span className="flex items-center gap-2"><ClipboardCheck size={13} className="text-[#F59E0B]" /> RMS REPORTS AWAITING SUPERVISORY REVIEW</span>
            <span className="font-mono text-[10px] text-[#9CA3AF]">{pendingReports.length} pending</span>
          </div>
          {pendingReports.length === 0 ? (
            <div className="p-3 font-mono text-[11px] text-[#6B7280]">No reports pending review. Submitted incident reports appear here instantly.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase">
                <tr><th className="px-3 py-2">RMS Case</th><th className="px-2">CASE/CFS</th><th className="px-2">Incident Type</th><th className="px-2">Reporting Officer</th><th className="px-2">Submitted</th><th className="px-2">Waiting</th></tr>
              </thead>
              <tbody>
                {pendingReports.map((r) => {
                  const waitMin = r.submitted_at ? Math.floor((Date.now() - new Date(r.submitted_at)) / 60000) : null;
                  const wait = waitMin === null ? "—" : waitMin >= 60 ? `${Math.floor(waitMin / 60)}h ${waitMin % 60}m` : `${waitMin}m`;
                  return (
                    <tr key={r.id} data-testid={`pending-report-${r.case_number}`} onClick={() => nav(`/rms-report/${r.id}`)}
                      className="border-b border-[#2C2D35] cursor-pointer hover:bg-[#1a1b20]">
                      <td className="px-3 py-1.5 font-mono text-[11px] font-bold text-[#F59E0B]">{r.case_number}</td>
                      <td className="px-2 font-mono text-[11px]">{r.cad_number}</td>
                      <td className="px-2 text-sm">{r.incident?.incident_type || r.incident?.call_type || "—"}</td>
                      <td className="px-2 text-sm">{r.reporting_officer?.name || r.created_by}</td>
                      <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}</td>
                      <td className="px-2 font-mono text-[11px]" style={{ color: waitMin > 240 ? "#DC2626" : waitMin > 60 ? "#F59E0B" : "#9CA3AF" }}>{wait}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-[#2C2D35] p-3">
            <div className="font-head text-xs font-semibold mb-3">INCIDENTS BY STATUS</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={statusData}>
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#1E1F24", border: "1px solid #2C2D35", color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="v">{statusData.map((e, i) => <Cell key={i} fill={SC[e.name] || "#3B82F6"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-[#2C2D35] p-3">
            <div className="font-head text-xs font-semibold mb-3">UNIT STATUS DISTRIBUTION</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={unitData}>
                <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#1E1F24", border: "1px solid #2C2D35", color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="v">{unitData.map((e, i) => <Cell key={i} fill={SC[e.name] || "#10B981"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-[#2C2D35] mt-4" data-testid="response-time-report">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center justify-between">
            <span>RESPONSE TIMES · DISPATCH → ON SCENE</span>
            <span className="font-mono text-[10px] text-[#9CA3AF]">OVERALL AVG {fmt(rt?.overall?.avg_seconds)} · n={rt?.overall?.count ?? 0}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3">
            {(!rt || rt.by_discipline.length === 0) && <div className="p-3 font-mono text-[11px] text-[#6B7280] sm:col-span-3">No dispatch→on-scene data yet. Populates as units mark on scene.</div>}
            {(rt?.by_discipline || []).map((d) => (
              <div key={d.discipline} className="p-3 border-r border-[#2C2D35]" data-testid={`rt-${d.discipline}`}>
                <div className="font-mono text-[10px] uppercase" style={{ color: DC[d.discipline] || "#9CA3AF" }}>{d.discipline}</div>
                <div className="font-mono font-bold text-2xl mt-1" style={{ color: DC[d.discipline] || "#F3F4F6" }}>{fmt(d.avg_seconds)}</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] mt-1">avg · {d.count} calls · fast {fmt(d.fastest_seconds)} · slow {fmt(d.slowest_seconds)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#2C2D35] mt-4">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">RECENT ACTIVITY</div>
          <table className="w-full text-left">
            <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase">
              <tr><th className="px-3 py-2">CC #</th><th className="px-2">Pri</th><th className="px-2">Type</th><th className="px-2">Disc</th><th className="px-2">Status</th><th className="px-2">Time</th></tr>
            </thead>
            <tbody>
              {(m?.recent || []).map((r) => (
                <tr key={r.cc_number} className="border-b border-[#2C2D35]">
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.cc_number}</td>
                  <td className="px-2"><PriorityBadge priority={r.priority} /></td>
                  <td className="px-2 text-sm">{r.call_type_label}</td>
                  <td className="px-2"><DiscBadge discipline={r.discipline} /></td>
                  <td className="px-2 font-mono text-[11px] uppercase">{r.status}</td>
                  <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{new Date(r.created_at).toLocaleTimeString("en-GB", { hour12: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-[#2C2D35] mt-4" data-testid="access-denial-log">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center justify-between">
            <span className="flex items-center gap-2"><ShieldAlert size={13} className="text-[#DC2626]" /> ACCESS DENIED · SECURITY COMPLIANCE AUDIT</span>
            <span className="font-mono text-[10px] text-[#9CA3AF]">{denials.length} recent</span>
          </div>
          {denials.length === 0 ? (
            <div className="p-3 font-mono text-[11px] text-[#6B7280]">No blocked access attempts recorded. Cross-unit and foreign-record attempts will appear here.</div>
          ) : (
            <table className="w-full text-left">
              <thead className="font-mono text-[10px] text-[#9CA3AF] uppercase">
                <tr><th className="px-3 py-2">Time</th><th className="px-2">User</th><th className="px-2">Unit</th><th className="px-2">Action</th><th className="px-2">Target</th><th className="px-2">Detail</th></tr>
              </thead>
              <tbody>
                {denials.map((d, i) => (
                  <tr key={i} className="border-b border-[#2C2D35]" data-testid={`denial-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-[#9CA3AF] whitespace-nowrap">{d.ts ? new Date(d.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "—"}</td>
                    <td className="px-2 text-sm">{d.user}</td>
                    <td className="px-2 font-mono text-[11px] text-[#F59E0B]">{d.badge || "—"}</td>
                    <td className="px-2 font-mono text-[10px] uppercase text-[#DC2626]">{d.action}</td>
                    <td className="px-2 font-mono text-[11px]">{d.target}</td>
                    <td className="px-2 font-mono text-[10px] text-[#9CA3AF]">{d.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
